import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    forwardRef
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';

interface SearchAutocompleteItem {
    id?: string | number;
    [key: string]: unknown;
}

@Component({
    selector: 'app-search-autocomplete',
    templateUrl: './search-autocomplete.component.html',
    styleUrls: ['./search-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchAutocompleteComponent),
            multi: true
        }
    ]
})
export class SearchAutocompleteComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() items: SearchAutocompleteItem[] = [];
    @Input() displayField = 'name';
    @Input() placeholder = '';
    @Input() disabled = false;
    @Input() minSearchLength = 4;
    @Input() debounceMs = 500;

    @Output() searchChange = new EventEmitter<string>();
    @Output() selectItem = new EventEmitter<SearchAutocompleteItem | null>();

    inputValue = '';
    isPanelOpen = false;
    private searchTerm$ = new Subject<string>();
    private searchSubscription: Subscription;
    private onChange: (value: SearchAutocompleteItem | null) => void = () => undefined;
    private onTouched: () => void = () => undefined;

    constructor(private elRef: ElementRef, private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.searchSubscription = this.searchTerm$.pipe(
            debounceTime(this.debounceMs),
            distinctUntilChanged()
        ).subscribe((term: string) => {
            const normalizedTerm = (term || '').trim();
            if (normalizedTerm.length < this.minSearchLength) {
                this.isPanelOpen = false;
                this.searchChange.emit('');
                this.cdr.markForCheck();
                return;
            }

            this.searchChange.emit(normalizedTerm);
        });
    }

    ngOnDestroy(): void {
        this.searchSubscription?.unsubscribe();
    }

    writeValue(value: SearchAutocompleteItem | null): void {
        this.inputValue = value ? this.resolveDisplayValue(value) : '';
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: SearchAutocompleteItem | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.cdr.markForCheck();
    }

    onInput(value: string): void {
        this.inputValue = value;
        this.onChange(null);
        this.selectItem.emit(null);

        const normalizedValue = (value || '').trim();
        this.isPanelOpen = normalizedValue.length >= this.minSearchLength;
        this.searchTerm$.next(normalizedValue);
        this.cdr.markForCheck();
    }

    onFocus(): void {
        if ((this.inputValue || '').trim().length >= this.minSearchLength && this.items?.length) {
            this.isPanelOpen = true;
            this.cdr.markForCheck();
        }
    }

    onSelect(item: SearchAutocompleteItem): void {
        this.inputValue = this.resolveDisplayValue(item);
        this.isPanelOpen = false;
        this.onChange(item);
        this.onTouched();
        this.selectItem.emit(item);
        this.cdr.markForCheck();
    }

    reset(): void {
        this.inputValue = '';
        this.isPanelOpen = false;
        this.onChange(null);
        this.selectItem.emit(null);
        this.cdr.markForCheck();
    }

    get shouldShowPanel(): boolean {
        return this.isPanelOpen
            && (this.inputValue || '').trim().length >= this.minSearchLength
            && !!this.items?.length;
    }

    trackByItem = (_index: number, item: SearchAutocompleteItem) => item?.id ?? item?.[this.displayField] ?? _index;

    @HostListener('focusout')
    onBlur(): void {
        this.onTouched();
    }

    private resolveDisplayValue(item: SearchAutocompleteItem | null): string {
        const value = item?.[this.displayField];
        return value !== undefined && value !== null ? String(value) : '';
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.isPanelOpen && !this.elRef.nativeElement.contains(event.target)) {
            this.isPanelOpen = false;
            this.cdr.markForCheck();
        }
    }
}
